import { useState, useEffect } from 'react';
import {
  useFloating as useFloatingDOM,
  offset,
  flip,
  shift,
  autoUpdate
} from '@floating-ui/react-dom';
import {
  useFloating,
  useDismiss,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager
} from '@floating-ui/react';
import type { VirtualElement } from '@floating-ui/dom';
import { Range } from 'slate';
import { ReactEditor } from 'slate-react';
import { AnimatePresence } from 'framer-motion';
import { SlashCommand } from './slash-commands-config';
import { AnimatedCommandList, AnimatedCommandButton } from '../utils/animated-components';
import { commandListAnimation } from '../utils/animation-variants';

interface SlashCommandListProps {
  editor: ReactEditor;
  commandText: string;
  targetRange: Range | null;
  onSelect: (command: SlashCommand) => void;
  commands: SlashCommand[];
  closeMenu: () => void;
}

export function SlashCommandList({
  editor,
  commandText,
  targetRange,
  onSelect,
  commands,
  closeMenu
}: SlashCommandListProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(true);

  // Reset selection when commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [commandText]);

  // Get the DOM range for positioning

  const getReferenceElement = (): VirtualElement | null => {
    if (!targetRange) return null;
    try {
      const domRange = ReactEditor.toDOMRange(editor, targetRange);
      const rect = domRange.getBoundingClientRect();
      const contextElement = domRange.startContainer.parentElement;
      if (!contextElement) return null;

      return {
        getBoundingClientRect: () => rect,
        contextElement
      };
    } catch (_error) {
      return null;
    }
  };

  // DOM positioning
  const { refs, floatingStyles } = useFloatingDOM({
    placement: 'bottom-start',
    middleware: [offset({ mainAxis: 8, alignmentAxis: 0 }), flip(), shift()],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: getReferenceElement() ?? undefined
    }
  });

  // Interaction handling
  const { context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) {
        closeMenu();
      }
    }
  });

  // Add dismiss interaction (without escape key handling)
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
    escapeKey: false // Disable escape key handling here since it's handled in useSlashCommands
  });

  // Set up interactions
  const { getFloatingProps } = useInteractions([dismiss]);

  // Filter commands based on input
  const filteredCommands = commands.filter((cmd) =>
    cmd.label.toLowerCase().includes(commandText.toLowerCase())
  );

  // Group commands by category
  const groupedCommands = filteredCommands.reduce<Record<string, SlashCommand[]>>(
    (acc, command) => {
      const category = command.category;
      acc[category] = acc[category] ?? [];
      acc[category].push(command);
      return acc;
    },
    {}
  );

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (filteredCommands.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(
            (prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length
          );
          break;
        case 'Enter':
          e.preventDefault();
          onSelect(filteredCommands[selectedIndex]);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [filteredCommands, selectedIndex, onSelect]);

  if (!targetRange || filteredCommands.length === 0) {
    return null;
  }

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} modal={false}>
        <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          <AnimatePresence>
            <AnimatedCommandList
              className="z-50 bg-popover rounded-lg border shadow-md overflow-hidden w-[340px]"
              variants={commandListAnimation}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div className="p-2">
                {Object.entries(groupedCommands).map(([category, categoryCommands]) => (
                  <div key={category}>
                    <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                      {category}
                    </div>
                    <div className="space-y-0.5">
                      {categoryCommands.map((command) => {
                        const index = filteredCommands.indexOf(command);
                        return (
                          <AnimatedCommandButton
                            key={command.label}
                            command={command}
                            isSelected={selectedIndex === index}
                            onSelect={() => {
                              onSelect(command);
                            }}
                            onHover={() => {
                              setSelectedIndex(index);
                            }}
                          />
                        );
                      })}
                    </div>
                    {category !== Object.keys(groupedCommands).slice(-1)[0] && (
                      <div className="h-px bg-border my-4" />
                    )}
                  </div>
                ))}
              </div>
            </AnimatedCommandList>
          </AnimatePresence>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
